import { createConfigParametersRepository } from '../../configParametersRepository';
import { STORAGE_KEY, EVENTS_STORAGE_KEY } from '../resources/constants';
import { v1 as uuidv1 } from 'uuid';
import { GtagEvent, StoredEvents } from './types';
import { gtagWrapper } from '../google-analytics/common';

const RECOVERED = 'recovered';

const getStorageClient = () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  return getTrackingStorageClient();
};

const getStoredEvents = (): StoredEvents => {
  return (
    getStorageClient()?.getFromStorage('sessionStorage', STORAGE_KEY)?.[
      EVENTS_STORAGE_KEY
    ] || {}
  );
};

const setStoredEvents = (events: StoredEvents = {}) => {
  const storageClient = getStorageClient();
  storageClient.update(EVENTS_STORAGE_KEY, events);
};

/**
 * Push an event to storage, if the storage is full, remove the oldest event
 * @param gtagEvent - ga4 event
 * @param maxStored - max number of events to store, default is 10
 * @returns uuid of the queued event
 */
export const pushStoredEvent = (
  gtagEvent: GtagEvent,
  maxStored = 10
): string | undefined => {
  if (gtagEvent[2]['misc'] === RECOVERED) {
    // in order to avoid infinite loops avoid queueing events being recovered
    return;
  }
  let storedEvents = getStoredEvents();
  const keys = Object.keys(storedEvents);
  // keys are already sorted because uuidv1 creates sequenced ids
  if (keys.length >= maxStored && maxStored > 0) {
    storedEvents = removeStoredEvent(keys[0]);
  }
  const uuid = uuidv1();
  storedEvents[uuid] = gtagEvent;
  setStoredEvents(storedEvents);
  return uuid;
};

/**
 * Remove an event from storage, returns the new storage
 * @param uuid - uuid of the event to remove
 * @returns new storage without the removed event
 */
export const removeStoredEvent = (uuid: string): StoredEvents => {
  const storedEvents = getStoredEvents();
  if (!storedEvents[uuid]) {
    return storedEvents;
  }
  const { [uuid]: _delete, ...newStoredEvents } = storedEvents;
  setStoredEvents(newStoredEvents);
  return newStoredEvents;
};

/**
 * Recover events stored and not triggered in previous pages
 * @param interval - interval in ms to wait between each event recovery, default is 1000ms
 */
export const recoverStoredEvents = (interval = 1000) => {
  const processNextEvent = () => {
    const storedEvents = getStoredEvents();
    const keys = Object.keys(storedEvents);
    if (!keys.length) {
      return;
    }
    const uuid = keys[0];
    const [command, eventName, params] = storedEvents[uuid];
    gtagWrapper(command, eventName, { ...params, misc: RECOVERED });
    removeStoredEvent(uuid);
    setTimeout(processNextEvent, interval);
  };
  processNextEvent();
};
